import { useState, useMemo } from "react";

// Constantes normativas 2026 (Perú)
const UIT_2026 = 5500;
const RMV_2026 = 1130;
const ASIGNACION_FAMILIAR = RMV_2026 * 0.10; // 10% de la RMV
const TASA_ONP = 0.13;
const PRIMA_SEGURO_AFP = 0.0137;
const APORTE_OBLIGATORIO_AFP = 0.10;

const AFPS = [
  { id: "habitat", nombre: "Habitat", comisionFlujo: 0.0147 },
  { id: "integra", nombre: "Integra", comisionFlujo: 0.0155 },
  { id: "prima", nombre: "Prima", comisionFlujo: 0.0160 },
  { id: "profuturo", nombre: "Profuturo", comisionFlujo: 0.0169 },
];

// Tramos progresivos de Renta de 5ta categoría (base: UIT 2026)
const TRAMOS_RENTA = [
  { limite: 5 * UIT_2026, tasa: 0.08 },
  { limite: 20 * UIT_2026, tasa: 0.14 },
  { limite: 35 * UIT_2026, tasa: 0.17 },
  { limite: 45 * UIT_2026, tasa: 0.20 },
  { limite: Infinity, tasa: 0.30 },
];

function calcularRenta5taAnual(ingresoAnualAfecto) {
  let restante = Math.max(0, ingresoAnualAfecto);
  let anterior = 0;
  let impuesto = 0;
  for (const tramo of TRAMOS_RENTA) {
    if (restante <= 0) break;
    const anchoTramo = tramo.limite - anterior;
    const montoEnTramo = Math.min(restante, anchoTramo);
    impuesto += montoEnTramo * tramo.tasa;
    restante -= montoEnTramo;
    anterior = tramo.limite;
  }
  return impuesto;
}

function formatearSoles(valor) {
  return valor.toLocaleString("es-PE", {
    style: "currency",
    currency: "PEN",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export default function CalculadoraSueldoNeto() {
  const [sueldoBruto, setSueldoBruto] = useState(3000);
  const [sistema, setSistema] = useState("afp"); // "afp" | "onp"
  const [afpId, setAfpId] = useState("habitat");
  const [tieneHijos, setTieneHijos] = useState(false);

  const resultado = useMemo(() => {
    const bruto = Number(sueldoBruto) || 0;
    const asignacion = tieneHijos ? ASIGNACION_FAMILIAR : 0;
    const computable = bruto + asignacion;

    let descuentoPension = 0;
    let detallePension = [];

    if (sistema === "onp") {
      descuentoPension = computable * TASA_ONP;
      detallePension = [{ etiqueta: "ONP (13%)", monto: descuentoPension }];
    } else {
      const afp = AFPS.find((a) => a.id === afpId) ?? AFPS[0];
      const aporte = computable * APORTE_OBLIGATORIO_AFP;
      const prima = computable * PRIMA_SEGURO_AFP;
      const comision = computable * afp.comisionFlujo;
      descuentoPension = aporte + prima + comision;
      detallePension = [
        { etiqueta: "Aporte obligatorio (10%)", monto: aporte },
        { etiqueta: "Prima de seguro (1.37%)", monto: prima },
        { etiqueta: `Comisión ${afp.nombre} (${(afp.comisionFlujo * 100).toFixed(2)}%)`, monto: comision },
      ];
    }

    // Renta de 5ta: proyección anual simplificada (gratificaciones exoneradas por Ley 29351)
    const ingresoAnualAfecto = computable * 12 - 7 * UIT_2026;
    const rentaAnual = calcularRenta5taAnual(ingresoAnualAfecto);
    const rentaMensual = rentaAnual / 12;

    const neto = computable - descuentoPension - rentaMensual;

    return {
      bruto,
      asignacion,
      computable,
      descuentoPension,
      detallePension,
      rentaMensual,
      neto,
      afectoRenta: ingresoAnualAfecto > 0,
    };
  }, [sueldoBruto, sistema, afpId, tieneHijos]);

  return (
    <div className="w-full min-h-full px-4 py-10 bg-stone-50">
      <div className="max-w-xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="flex items-center justify-center text-lg font-semibold text-white bg-red-700 rounded-full h-11 w-11">
            S/
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900">
              Calculadora de Sueldo Neto
            </h1>
            <p className="text-sm text-slate-500">Perú 2026 · AFP, ONP y Renta de 5ta</p>
          </div>
        </div>

        <div className="overflow-hidden bg-white border shadow-sm rounded-2xl border-slate-200">
          <div className="p-6 space-y-5 border-b border-slate-100">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Sueldo bruto mensual
              </label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 font-mono text-sm">
                  S/
                </span>
                <input
                  type="number"
                  min="0"
                  value={sueldoBruto}
                  onChange={(e) => setSueldoBruto(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-4 py-2.5 text-slate-900 font-mono text-lg focus:outline-none focus:ring-2 focus:ring-red-700/30 focus:border-red-700"
                  placeholder="3000"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Sistema de pensión
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setSistema("afp")}
                  className={`rounded-lg border py-2.5 text-sm font-medium transition-colors ${
                    sistema === "afp"
                      ? "border-red-700 bg-red-50 text-red-800"
                      : "border-slate-300 text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  AFP
                </button>
                <button
                  type="button"
                  onClick={() => setSistema("onp")}
                  className={`rounded-lg border py-2.5 text-sm font-medium transition-colors ${
                    sistema === "onp"
                      ? "border-red-700 bg-red-50 text-red-800"
                      : "border-slate-300 text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  ONP
                </button>
              </div>
            </div>

            {sistema === "afp" && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  AFP
                </label>
                <select
                  value={afpId}
                  onChange={(e) => setAfpId(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-slate-900 focus:outline-none focus:ring-2 focus:ring-red-700/30 focus:border-red-700"
                >
                  {AFPS.map((afp) => (
                    <option key={afp.id} value={afp.id}>
                      {afp.nombre} — comisión {(afp.comisionFlujo * 100).toFixed(2)}%
                    </option>
                  ))}
                </select>
              </div>
            )}

            <label className="flex items-center gap-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={tieneHijos}
                onChange={(e) => setTieneHijos(e.target.checked)}
                className="w-4 h-4 text-red-700 rounded border-slate-300 focus:ring-red-700/30"
              />
              <span className="text-sm text-slate-700">
                Recibo asignación familiar ({formatearSoles(ASIGNACION_FAMILIAR)})
              </span>
            </label>
          </div>

          <div className="p-6 bg-slate-900">
            <p className="mb-1 text-xs tracking-wide uppercase text-slate-400">
              Sueldo neto estimado
            </p>
            <p className="mb-5 font-mono text-4xl font-bold text-white">
              {formatearSoles(Math.max(0, resultado.neto))}
            </p>

            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between text-slate-300">
                <span>Bruto {resultado.asignacion > 0 && "+ asignación familiar"}</span>
                <span className="font-mono">{formatearSoles(resultado.computable)}</span>
              </div>
              {resultado.detallePension.map((item, i) => (
                <div key={i} className="flex justify-between text-slate-400">
                  <span>− {item.etiqueta}</span>
                  <span className="font-mono">−{formatearSoles(item.monto)}</span>
                </div>
              ))}
              <div className="flex justify-between text-slate-400">
                <span>− Renta de 5ta categoría</span>
                <span className="font-mono">
                  {resultado.afectoRenta
                    ? `−${formatearSoles(resultado.rentaMensual)}`
                    : "S/ 0.00"}
                </span>
              </div>
            </div>
          </div>
        </div>

        <p className="mt-4 text-xs leading-relaxed text-slate-400">
          Herramienta orientativa. Los montos son estimaciones basadas en la normativa
          general vigente en 2026 (UIT S/ {UIT_2026.toLocaleString("es-PE")}, RMV S/{" "}
          {RMV_2026.toLocaleString("es-PE")}) y pueden diferir del cálculo real de tu
          empleador. No constituye asesoría legal, contable ni tributaria.
        </p>
      </div>
    </div>
  );
}